// The one way a staff screen takes a telephone number: Uzbek only, the +998 in
// front fixed, and the nine national digits typed into a mask that groups
// them the way people read them aloud — 90 123 45 67.

import { useLayoutEffect, useRef } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent } from 'react'

import { Flag, useT } from '../i18n'
import common from '../i18n/common'

/** How many digits an Uzbek number has after +998. */
export const UZ_DIGITS = 9

/** The nine national digits as the international number the API stores. */
export function toE164Uz(digits: string): string {
  return `+998${nationalDigits(digits)}`
}

/**
 * The national digits in whatever was typed or pasted: a whole number with
 * +998 or 998 in front loses it, and anything past nine digits is dropped.
 */
export function nationalDigits(raw: string): string {
  let d = raw.replace(/\D/g, '')
  if (d.length > UZ_DIGITS && d.startsWith('998')) d = d.slice(3)
  return d.slice(0, UZ_DIGITS)
}

/** 901234567 → "90 123 45 67"; a partial number is grouped as far as it goes. */
export function formatUzPhone(digits: string): string {
  const d = nationalDigits(digits)
  const parts = [d.slice(0, 2), d.slice(2, 5), d.slice(5, 7), d.slice(7, 9)]
  return parts.filter(Boolean).join(' ')
}

/** Where the caret sits after `count` digits in the grouped form. */
function caretAfter(formatted: string, count: number): number {
  if (count <= 0) return 0
  let seen = 0
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i]!)) seen++
    if (seen === count) return i + 1
  }
  return formatted.length
}

export function PhoneInput({
  value,
  onChange,
  autoFocus,
  id,
  disabled,
  invalid,
  label,
  onEnter,
}: {
  /** The national digits, up to nine. */
  value: string
  onChange: (digits: string) => void
  autoFocus?: boolean
  id?: string
  disabled?: boolean
  /** Marks the field as wrong, for a form that has checked it. */
  invalid?: boolean
  /** What a screen reader calls the field; "Phone number" by default. */
  label?: string
  onEnter?: () => void
}) {
  const t = useT(common)
  const input = useRef<HTMLInputElement>(null)
  // Where the caret belongs after the next render, counted in digits, so that
  // editing in the middle of the number does not throw it to the end when the
  // spaces move.
  const caret = useRef<number | null>(null)
  const shown = formatUzPhone(value)

  useLayoutEffect(() => {
    const el = input.current
    if (caret.current === null || !el || document.activeElement !== el) return
    const at = caretAfter(shown, caret.current)
    el.setSelectionRange(at, at)
    caret.current = null
  }, [shown])

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onEnter) {
      e.preventDefault()
      onEnter()
      return
    }
    // The spaces are drawn, not typed: deleting towards one deletes the digit
    // beyond it instead of doing nothing.
    const el = e.currentTarget
    const at = el.selectionStart ?? 0
    if (at !== el.selectionEnd) return
    if (e.key === 'Backspace' && at > 0 && el.value[at - 1] === ' ') el.setSelectionRange(at - 1, at - 1)
    if (e.key === 'Delete' && el.value[at] === ' ') el.setSelectionRange(at + 1, at + 1)
  }

  return (
    <div className={invalid ? 'phone-input is-invalid' : 'phone-input'} data-disabled={disabled || undefined}>
      <span className="phone-input__prefix" title={t('phoneCountry')}>
        <Flag code="uz" />
        <span className="mono">+998</span>
      </span>
      <input
        ref={input}
        id={id}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        className="phone-input__field mono"
        placeholder="90 123 45 67"
        value={shown}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-label={label ?? t('phoneNumber')}
        aria-invalid={invalid || undefined}
        onKeyDown={onKeyDown}
        onChange={(e) => {
          const el = e.target
          const before = el.value.slice(0, el.selectionStart ?? el.value.length)
          const next = nationalDigits(el.value)
          // A paste of a whole number puts the caret at its end.
          caret.current = Math.min(before.replace(/\D/g, '').length, next.length)
          if (el.value.replace(/\D/g, '').length > UZ_DIGITS) caret.current = next.length
          onChange(next)
        }}
      />
    </div>
  )
}
